import { AIMessage, ToolMessage } from '@langchain/langgraph-sdk'
import { t } from '@lingui/macro'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { useStreamContext } from '../../../providers/Stream'

function isComplexValue(value: any): boolean {
    return Array.isArray(value) || (typeof value === 'object' && value !== null)
}

// Helper function to check if a tool call is pending (no corresponding ToolResult yet)
function isPendingToolCall(toolCallId: string, messages: any[]): boolean {
    return !messages.some((message) => message.type === 'tool' && message.tool_call_id === toolCallId)
}

// Pulsing animation variants for framer-motion
const pulseVariants = {
    pulse: {
        boxShadow: [
            '0 0 0 0 rgba(59, 130, 246, 0.4)', // eslint-disable-line string-to-lingui/missing-lingui-transformation
            '0 0 0 8px rgba(59, 130, 246, 0)', // eslint-disable-line string-to-lingui/missing-lingui-transformation
            '0 0 0 0 rgba(59, 130, 246, 0)' // eslint-disable-line string-to-lingui/missing-lingui-transformation
        ],
        transition: {
            duration: 1.5,
            repeat: Infinity,
            repeatType: 'loop' as const
        }
    }
}

export function ToolCalls({ toolCalls }: { toolCalls: AIMessage['tool_calls'] }) {
    const { messages } = useStreamContext()

    if (!toolCalls || toolCalls.length === 0) return null

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateRows: '1fr auto',
                gap: '4px',
                maxWidth: '800px',
                width: '100%',
                margin: '4px auto',
                backgroundColor: 'transparent'
            }}
        >
            {toolCalls.map((tc, idx) => {
                const args = tc.args as Record<string, any>
                const hasArgs = Object.keys(args).length > 0
                const isPending = tc.id ? isPendingToolCall(tc.id, messages) : false

                return (
                    <motion.div
                        key={idx}
                        variants={pulseVariants}
                        animate={isPending ? 'pulse' : undefined}
                        style={{
                            overflow: 'hidden',
                            borderRadius: '6px',
                            border: `1px solid ${isPending ? '#3b82f6' : '#e5e7eb'}`,
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            backgroundColor: '#ffffff'
                        }}
                    >
                        <div
                            style={{
                                borderBottom: '1px solid #e5e7eb',
                                backgroundColor: isPending ? '#eff6ff' : '#f9fafb',
                                padding: '6px 12px'
                            }}
                        >
                            <h3
                                style={{
                                    fontWeight: '500',
                                    color: isPending ? '#1d4ed8' : '#111827',
                                    fontSize: '14px',
                                    margin: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {isPending && (
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            border: '2px solid #3b82f6',
                                            borderTop: '2px solid transparent',
                                            borderRadius: '50%'
                                        }}
                                    />
                                )}
                                {tc.name}
                                {tc.id && (
                                    <code
                                        style={{
                                            marginLeft: '6px',
                                            borderRadius: '3px',
                                            backgroundColor: isPending ? '#dbeafe' : '#f3f4f6',
                                            padding: '1px 4px',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {tc.id}
                                    </code>
                                )}
                                {isPending && (
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            color: '#6b7280',
                                            fontWeight: '400',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        {t`Running...`}
                                    </span>
                                )}
                            </h3>
                        </div>
                        {hasArgs ? (
                            <table
                                style={{
                                    minWidth: '100%',
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                    borderBottom: '1px solid #e5e7eb'
                                }}
                            >
                                <tbody>
                                    {Object.entries(args).map(([key, value], argIdx) => (
                                        <tr
                                            key={argIdx}
                                            style={{
                                                borderBottom: argIdx < Object.entries(args).length - 1 ? '1px solid #e5e7eb' : 'none'
                                            }}
                                        >
                                            <td
                                                style={{
                                                    padding: '5px 12px',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    whiteSpace: 'nowrap',
                                                    color: '#111827'
                                                }}
                                            >
                                                {key}
                                            </td>
                                            <td
                                                style={{
                                                    padding: '5px 12px',
                                                    fontSize: '13px',
                                                    color: '#6b7280'
                                                }}
                                            >
                                                {isComplexValue(value) ? (
                                                    <code
                                                        style={{
                                                            borderRadius: '3px',
                                                            backgroundColor: '#f9fafb',
                                                            padding: '1px 4px',
                                                            fontFamily: 'monospace',
                                                            fontSize: '13px',
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        {JSON.stringify(value, null, 2)}
                                                    </code>
                                                ) : (
                                                    String(value)
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <code
                                style={{
                                    display: 'block',
                                    padding: '6px',
                                    fontSize: '13px'
                                }}
                            >
                                {t`{}`}
                            </code>
                        )}
                    </motion.div>
                )
            })}
        </div>
    )
}

export function ToolResult({ message }: { message: ToolMessage }) {
    const [isExpanded, setIsExpanded] = useState(false)

    let parsedContent: any
    let isJsonContent = false

    try {
        if (typeof message.content === 'string') {
            parsedContent = JSON.parse(message.content)
            isJsonContent = isComplexValue(parsedContent)
        }
    } catch {
        // Content is not JSON, use as is
        parsedContent = message.content
    }

    const contentStr = isJsonContent ? JSON.stringify(parsedContent, null, 2) : String(message.content)
    const contentLines = contentStr.split('\n')
    const shouldTruncate = contentLines.length > 4 || contentStr.length > 500
    const displayedContent =
        shouldTruncate && !isExpanded
            ? contentStr.length > 500
                ? contentStr.slice(0, 500) + '...'
                : contentLines.slice(0, 4).join('\n') + '\n...'
            : contentStr

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateRows: '1fr auto',
                gap: '4px',
                maxWidth: '800px',
                width: '100%',
                margin: '4px auto',
                backgroundColor: 'transparent'
            }}
        >
            <div
                style={{
                    overflow: 'hidden',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    backgroundColor: '#ffffff'
                }}
            >
                <div
                    style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        padding: '6px 12px'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                        }}
                    >
                        {message.name ? (
                            <h3
                                style={{
                                    fontWeight: '500',
                                    color: '#111827',
                                    fontSize: '14px',
                                    margin: '0'
                                }}
                            >
                                {t`Tool Result`}:{' '}
                                <code
                                    style={{
                                        borderRadius: '3px',
                                        backgroundColor: '#f3f4f6',
                                        padding: '1px 4px'
                                    }}
                                >
                                    {message.name}
                                </code>
                            </h3>
                        ) : (
                            <h3
                                style={{
                                    fontWeight: '500',
                                    color: '#111827',
                                    fontSize: '14px',
                                    margin: '0'
                                }}
                            >
                                {t`Tool Result`}
                            </h3>
                        )}
                        {message.tool_call_id && (
                            <code
                                style={{
                                    marginLeft: '6px',
                                    borderRadius: '3px',
                                    backgroundColor: '#f3f4f6',
                                    padding: '1px 4px',
                                    fontSize: '13px'
                                }}
                            >
                                {message.tool_call_id}
                            </code>
                        )}
                    </div>
                </div>
                <motion.div
                    style={{
                        minWidth: '100%',
                        backgroundColor: '#f3f4f6'
                    }}
                    initial={false}
                    animate={{ height: 'auto' }}
                    transition={{ duration: 0.3 }}
                >
                    <div style={{ padding: '6px' }}>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.div
                                key={isExpanded ? 'expanded' : 'collapsed'}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                {isJsonContent ? (
                                    <table
                                        style={{
                                            minWidth: '100%',
                                            borderCollapse: 'separate',
                                            borderSpacing: 0
                                        }}
                                    >
                                        <tbody>
                                            {(Array.isArray(parsedContent)
                                                ? isExpanded
                                                    ? parsedContent
                                                    : parsedContent.slice(0, 5)
                                                : Object.entries(parsedContent)
                                            ).map((item, argIdx) => {
                                                const [key, value] = Array.isArray(parsedContent) ? [argIdx, item] : [item[0], item[1]]
                                                return (
                                                    <tr
                                                        key={argIdx}
                                                        style={{
                                                            borderBottom:
                                                                argIdx <
                                                                (Array.isArray(parsedContent)
                                                                    ? isExpanded
                                                                        ? parsedContent.length
                                                                        : Math.min(5, parsedContent.length)
                                                                    : Object.entries(parsedContent).length) -
                                                                    1
                                                                    ? '1px solid #e5e7eb'
                                                                    : 'none'
                                                        }}
                                                    >
                                                        <td
                                                            style={{
                                                                padding: '5px 12px',
                                                                fontSize: '13px',
                                                                fontWeight: '500',
                                                                whiteSpace: 'nowrap',
                                                                color: '#111827'
                                                            }}
                                                        >
                                                            {key}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding: '5px 12px',
                                                                fontSize: '13px',
                                                                color: '#6b7280'
                                                            }}
                                                        >
                                                            {isComplexValue(value) ? (
                                                                <code
                                                                    style={{
                                                                        borderRadius: '3px',
                                                                        backgroundColor: '#f9fafb',
                                                                        padding: '1px 4px',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '13px',
                                                                        wordBreak: 'break-all'
                                                                    }}
                                                                >
                                                                    {JSON.stringify(value, null, 2)}
                                                                </code>
                                                            ) : (
                                                                String(value)
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <code
                                        style={{
                                            display: 'block',
                                            padding: '6px',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {displayedContent}
                                    </code>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    {((shouldTruncate && !isJsonContent) || (isJsonContent && Array.isArray(parsedContent) && parsedContent.length > 5)) && (
                        <motion.button
                            onClick={() => setIsExpanded(!isExpanded)}
                            style={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderTop: '1px solid #e5e7eb',
                                padding: '6px 0',
                                color: '#6b7280',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                                backgroundColor: 'transparent'
                            }}
                            initial={{ scale: 1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {isExpanded ? <ChevronUp /> : <ChevronDown />}
                        </motion.button>
                    )}
                </motion.div>
            </div>
        </div>
    )
}
